// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const UserAvatar = require('client-v2/common/components/user-avatar')
const { CapsuleButton } = require('client-v2/common/components/buttons')
const { FetchStateEnum } = require('client-v2/common/utils/enums')
const EmojiMentionsInput = require('client-v2/common/components/emoji-mentions-input-lazy')
const InputCheckbox = require('client-v2/common/components/input-checkbox')
const FollowPeopleSocialAuthCard = require('client-v2/logged-in-landing/containers/follow-people-social-auth-card/component-smart')

const bem = BemEntity('ShareModalShareFriends')

const MAX_COMMENT_LENGTH = 200

function ShareModalShareFriends(props) {
  const {
    friendsCount,
    friendsSearch,
    friendsState,
    isSocialAuthCardVisible,
    searchText,
    submitState,
    user
  } = props
  const isSubmitted = submitState !== FetchStateEnum.EMPTY
  const isSent = submitState === FetchStateEnum.LOADED
  const isLoading = (
    friendsState === FetchStateEnum.LOADING ||
    submitState === FetchStateEnum.LOADING
  )

  return (
    tag('div', {
      className: classNames(
        props.className,
        bem.block()
      )
    },
      tag('div', {
        className: bem.element('inner', {
          friends: friendsCount !== 0
        })
      },
        tag(Header),
        tag(SearchInput, {
          friendsCount,
          isSubmitted,
          value: searchText,
          onChange: props.onFriendsSearch
        }),
        tag(FriendsList, {
          disabled: isSubmitted,
          friendsCount,
          friendsSearch,
          isLoading,
          isSent,
          isSocialAuthCardVisible,
          onFriendClick: props.onFriendClick
        })
      ),
      tag(MessageForm, {
        friendsCount,
        friendsSearch,
        isSubmitted,
        user,
        onSubmit: props.onFriendsShare
      })
    )
  )
}

ShareModalShareFriends.propTypes = {
  // Values
  className: PropTypes.string,
  friends: PropTypes.arrayOf(PropTypes.object).isRequired,
  friendsState: PropTypes.string.isRequired,
  searchText: PropTypes.string.isRequired,
  submitState: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  // Callbacks
  onFriendClick: PropTypes.func.isRequired,
  onFriendsSearch: PropTypes.func.isRequired,
  onFriendsShare: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired
}

module.exports = ShareModalShareFriends

// BEM Block entity Element components

function SuccessMessage() {
  return (
    tag('div', {
      className: bem.element('success')
    },
      tag('div', {
        className: bem.element('success-sent')
      }),
      tag('div', {
        className: bem.element('success-message')
      },
        'Shared successfully!'
      )
    )
  )
}

function Header() {
  return (
    tag('div', {
      className: bem.element('message')
    },
      'Send to friends'
    )
  )
}

function FriendsList(props) {
  if (props.isLoading) {
    return (
      tag('div', {
        className: bem.element('loading-indicator')
      })
    )
  }

  if (props.isSent) {
    return tag(SuccessMessage)
  }

  const { friendsCount, friendsSearch } = props
  if (friendsCount === 0) {
    return ([
      tag('div', {
        className: bem.element('empty-message')
      },
        tag('div', {
          className: bem.element('empty-title')
        },
          `Nobody's here yet.`
        ),
        tag('div', {
          className: bem.element('empty-subtitle')
        },
          `You can share directly with friends you are following who follow you back.`
        )
      ),
      props.isSocialAuthCardVisible && tag('div', {
        className: bem.element('follow-people')
      },
        'Find your friends on Mix'
      ),
      tag(FollowPeopleSocialAuthCard)
    ])
  }

  return (
    tag('div', {
      className: bem.element('friends-list')
    },
      friendsSearch.map(friend => (
        tag(Friend, {
          key: `share-friend:${friend.id}`,
          disabled: props.disabled,
          friend,
          onClick: props.onFriendClick
        })
      ))
    )
  )
}

FriendsList.propTypes = {
  // Values
  disabled: PropTypes.bool.isRequired,
  friends: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSent: PropTypes.bool.isRequired,
  isSocialAuthCardVisible: PropTypes.bool.isRequired,
  // Callbacks
  onFriendClick: PropTypes.func.isRequired
}

function Friend(props) {
  const { friend } = props
  return (
    tag('div', {
      className: bem.element('friend'),
      onClick: () => props.onClick(friend.id)
    },
      tag(UserAvatar, {
        className: bem.element('friend-icon'),
        href: true,
        user: friend
      }),
      tag('div', {
        className: bem.element('friend-content')
      },
        tag('div', {
          className: bem.element('friend-name')
        },
          friend['display_name']
        ),
        tag('div', {
          className: bem.element('friend-username')
        },
          `@${friend.username}`
        )
      ),
      tag(InputCheckbox, {
        className: bem.element('friend-checkbox'),
        checked: friend.isSelected,
        disabled: props.disabled,
        readOnly: true
      })
    )
  )
}

Friend.propTypes = {
  // Values
  disabled: PropTypes.bool,
  friend: PropTypes.object.isRequired,
  // Callbacks
  onClick: PropTypes.func.isRequired
}

function SearchInput(props) {
  const { friendsCount, isSubmitted } = props
  if (friendsCount === 0 || isSubmitted) {
    return null
  }
  return (
    tag('input', {
      className: bem.element('search-input'),
      placeholder: 'Search',
      onChange: event => props.onChange(event.target.value),
      value: props.value
    })
  )
}

SearchInput.propTypes = {
  // Values
  isSubmitted: PropTypes.bool.isRequired,
  value: PropTypes.string.isRequired,
  // Callbacks
  onChange: PropTypes.func.isRequired
}

function MessageForm(props) {
  const [ messageText, setMessageText ] = React.useState('')

  const onReset = event => {
    event.preventDefault()
    setMessageText('')
  }

  const onSubmit = event => {
    event.preventDefault()
    props.onSubmit(messageText)
    setMessageText('')
  }

  const onInputChange = (event, newValue, newPlainTextValue) => {
    setMessageText(newPlainTextValue)
  }

  const { friendsCount, friendsSearch } = props
  const selectedFriends = friendsSearch.filter(f => f.isSelected)
  const selectedCount = selectedFriends.length

  const submitLabelText = () => {
    if (props.isSubmitted) {
      return 'Sharing...'
    }
    if (!selectedCount) {
      return 'Select friends'
    }
    if (selectedCount === 1) {
      const [ friend ] = selectedFriends
      return `Share privately with ${friend['display_name']}`
    }
    return `Share privately with ${selectedCount} people`
  }

  if (friendsCount === 0 || props.isSubmitted) {
    return null
  }

  return (
    tag('form', {
      className: bem.element('form'),
      onReset,
      onSubmit
    },
      tag(UserAvatar, {
        className: bem.element('form-icon'),
        badge: false,
        user: props.user,
        visible: true
      }),
      tag(EmojiMentionsInput, {
        disabled: props.isSubmitted,
        maxLength: MAX_COMMENT_LENGTH,
        placeholder: 'Add a private message...',
        popperPlacement: 'top',
        value: messageText,
        onChange: onInputChange
      }),
      tag(CapsuleButton, {
        className: bem.element('form-submit'),
        type: 'submit',
        disabled: selectedCount === 0
      },
        submitLabelText()
      )
    )
  )
}

MessageForm.propTypes = {
  // Values
  friends: PropTypes.arrayOf(PropTypes.object).isRequired,
  isSubmitted: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  // Callbacks
  onSubmit: PropTypes.func.isRequired
}
